import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { faBars, faXmark } from "@fortawesome/pro-solid-svg-icons";

import { ROUTE_CONFIG } from "@/routes/config";
import { trackCheckCtaClick } from "@/utils/analytics";

import { Button } from "../Button/Button";
import { ButtonColor, ButtonSize } from "../Button/types";
import { Icon } from "../Icon/Icon";
import { Logo } from "../Logo/Logo";
import { MaconiaLogo } from "../MaconiaLogo/MaconiaLogo";
import { Footer } from "../Footer/Footer";

const { LOGIN, HOME, PRODUCT, PRICING, ABOUT, CONTACT } = ROUTE_CONFIG;

const NAV_ITEMS = [
	{ to: PRODUCT, key: "product" },
	{ to: PRICING, key: "pricing" },
	{ to: ABOUT, key: "about" },
	{ to: CONTACT, key: "contact" },
];

export const QuestionnaireLayout = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const handleStartCheck = () => {
		setMobileMenuOpen(false);
		trackCheckCtaClick("header");
		navigate("/questionnaire/yes");
	};

	// accent-strong, not accent-dark: #D9440F is 4.39:1 and reads like a pass without
	// being one. accent-strong is the only orange that may be a label (guide §2.2).
	const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
		`font-urbanist text-[16px] font-medium hover:text-accent-strong transition-colors ${
			isActive ? "text-accent-strong font-semibold" : "text-ink"
		}`;

	// Hide the check CTA while the visitor is already inside the check
	const isInQuestionnaire = pathname.startsWith("/questionnaire");

	return (
		<div className="w-[100vw] h-[100vh] relative font-urbanist" id="portal-auth">
			<div className="h-[92px] w-full border-b border-primary-gray-lightest flex items-center justify-between p-6 gap-4">
				<div className="flex gap-3 items-center justify-center w-fit shrink-0">
					<Logo handleClick={() => navigate(HOME)} />
					<div className="gap-2 items-center justify-center w-fit hidden sm:flex flex-row">
						<span className="text-xs text-primary-gray-lighter font-normal">Ein Produkt von</span>
						<MaconiaLogo handleClick={() => window.open("https://www.maconia.de", "_blank")} />
					</div>
				</div>

				<nav className="hidden lg:flex flex-row items-center gap-8">
					{NAV_ITEMS.map((item) => (
						<NavLink key={item.key} className={navLinkClasses} to={item.to}>
							{t(`marketing.nav.${item.key}`)}
						</NavLink>
					))}
				</nav>

				<div className="hidden lg:flex flex-row items-center w-fit gap-2 shrink-0">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						title={t("marketing.nav.login")}
						onClick={() => navigate(LOGIN)}
					/>
					{!isInQuestionnaire && (
						<Button
							size={ButtonSize.ML}
							title={t("marketing.nav.startCheck")}
							onClick={handleStartCheck}
						/>
					)}
				</div>

				<button
					aria-label={t("marketing.nav.menu")}
					className="lg:hidden p-2"
					type="button"
					onClick={() => setMobileMenuOpen((open) => !open)}
				>
					<Icon className="w-6 h-6 text-ink" icon={mobileMenuOpen ? faXmark : faBars} />
				</button>
			</div>

			{mobileMenuOpen && (
				<div className="lg:hidden absolute top-[92px] left-0 right-0 z-50 bg-white border-b border-primary-gray-lightest flex flex-col gap-4 p-6 shadow-lg">
					{NAV_ITEMS.map((item) => (
						<NavLink
							key={item.key}
							className={navLinkClasses}
							to={item.to}
							onClick={() => setMobileMenuOpen(false)}
						>
							{t(`marketing.nav.${item.key}`)}
						</NavLink>
					))}
					<div className="flex flex-col gap-2 pt-2 border-t border-primary-gray-lightest">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							title={t("marketing.nav.login")}
							onClick={() => {
								setMobileMenuOpen(false);
								navigate(LOGIN);
							}}
						/>
						{!isInQuestionnaire && (
							<Button
								size={ButtonSize.ML}
								title={t("marketing.nav.startCheck")}
								onClick={handleStartCheck}
							/>
						)}
					</div>
				</div>
			)}

			<div className="min-h-[calc(100%-142px)] w-full">
				<Outlet />
			</div>
			<Footer />
		</div>
	);
};
